import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { deleteSeries } from '@/api/series'
import {
  seriesKeys,
  moveConnectionsKeys,
  taggingsKeys,
  dashboardKeys,
} from '@/api/queryKeys'
import { useToast } from '@/hooks/use-toast'

/**
 * Hook to delete a series
 *
 * Handles:
 * - API call to soft delete series
 * - Cache invalidation for all series queries
 * - Navigation back to series list
 * - Success/error toast notifications
 * - Telemetry events
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const deleteSeries = useDeleteSeries()
 *
 * const handleDelete = async (seriesId: string) => {
 *   await deleteSeries.mutateAsync(seriesId)
 * }
 * ```
 */
export function useDeleteSeries() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (seriesId: string) => deleteSeries(seriesId),
    onSuccess: (_data, _seriesId) => {
      // Invalidate all affected queries (backend cascade deletes series_moves, move_connections, taggings)
      queryClient.invalidateQueries({ queryKey: seriesKeys.all })
      queryClient.invalidateQueries({ queryKey: moveConnectionsKeys.all })
      queryClient.invalidateQueries({ queryKey: taggingsKeys.all })
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all })

      // Show success toast
      toast({
        title: 'Series deleted',
        description: 'The series has been deleted successfully.',
      })

      // Navigate back to series list
      navigate('/series')
    },
    onError: (error: Error) => {
      // Show error toast
      toast({
        title: 'Failed to delete series',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      })
    },
  })
}
